import { unwrapResult } from "@reduxjs/toolkit";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { BASE_URL } from "../../../../api/baseURL";
import forwardarrow from "../../../../assets/images/arrow_forwardb.svg";
import Button from "../../../../components/common/Button/Button";
import CustomCheckbox from "../../../../components/common/customCheckbox/customCheckbox";
import Input from "../../../../components/common/Input/Input";
import TitleDescription from "../../../../components/newMotorPolicy/titleDescription/titleDescription";
import Summary from "../../../../components/newMotorPolicy/veiwSummaryPopup/summary";
import { PROMO_CODE } from "../../../../components/newMotorPolicy/veiwSummaryPopup/viewSummaryPopup";
import useLanguageTranslate from "../../../../hooks/useLanguageTranslate";
import useMotorForm from "../../../../hooks/useMotorFormData";
import {
  endEndorsement,
  getAdjustSumInsuredResponse,
  getEndEndorsementLoading,
} from "../../../../redux/features/endorsements/cancellationSlice";
import {
  getPolicyPayLoading,
  policyPayAdjustSumInsured,
} from "../../../../redux/features/payment/policyPaySlice";
import { getPolicyViewResponse } from "../../../../redux/features/policyDetailsSlice";
import { selectProfileData } from "../../../../redux/features/profileSlice";
import { getClientSource } from "../../../../utils/getClientSource";
import ReviewMotorTable from "../../renewalMotorPolicy/reviewAndConfirmPage/reviewMotorTable";
import AdjustSumInsuredMotorLayout from "../adjustSumInsuredMotorLayout";

const ReviewAndConfirmPage = () => {
  /* Setting up dispatch to call redux */
  const dispatch = useDispatch();

  /* Setting up variables with appropriate requests to send and response to receive, reading from redux/functions */

  const navigate = useNavigate();
  const { t } = useTranslation();
  const [formState, setFormState] = useState({});
  const translate = useLanguageTranslate();

  const { saveMotorDataInRedux, removeMotorFormData } = useMotorForm(
    PROMO_CODE,
    setFormState
  );

  const applyPromoCode = () => {
    saveMotorDataInRedux(formState);
  };

  const [isChecked, setIsChecked] = useState(false);
  const declaration = t(
    "motorInsurance.changePlanMotorPolicy.reviewAndConfirmPage.declaration",
    { returnObjects: true }
  );

  const policyViewResponseDetails = useSelector(getPolicyViewResponse);
  const policyPayLoading = useSelector(getPolicyPayLoading);
  const adjustSumInsuredResponse = useSelector(getAdjustSumInsuredResponse);

  const policyEndEndorsementLoading = useSelector(getEndEndorsementLoading);

  const profileData = useSelector(selectProfileData);

  // Setting up policy pay
  // const quoteNumber = policyViewResponseDetails.PolicyHeader?.QuoteNumber
  const policyNumber = policyViewResponseDetails.PolicyHeader?.PolicyNumber;
  const vehicleDetails = `${policyViewResponseDetails.PolicyHeader?.Make} ${policyViewResponseDetails.PolicyHeader?.Model} ${policyViewResponseDetails.PolicyHeader?.ModelYear}`;
  const originalSumInsured =
    policyViewResponseDetails.PolicyHeader?.OldSumInsured;

  const totalPremium = adjustSumInsuredResponse?.EndorsementFeesTotal;
  /* Next / Payment */
  const handlePayment = async () => {
    if (policyNumber && totalPremium) {
      const payload = {
        PolicyNumber: policyNumber,
        Premium: totalPremium,
        Channel: getClientSource(),
        uatAmount: `${totalPremium}` ?? "",
        newSumInsured: `${adjustSumInsuredResponse?.NewPremium}` ?? "",
      };

      try {
        // Calling API policy/pay
        const linkAction = await dispatch(policyPayAdjustSumInsured(payload));
        const link = unwrapResult(linkAction);
        // console.log("Link is: ", link)
        if (link) {
          window.location.href = link;
        }
      } catch (error) {
        // alert("Error in getting payment link: ", error)
        toast.error("Error in getting payment link: ", error);
      }
    }
  };

  const handleNoPayment = async () => {
    if (policyNumber) {
      const payload = {
        PolicyNumber: policyNumber,
        EndorsementType: "VVC",
      };

      const emailPayload = {
        PolicyNumber: policyNumber,
        CustomerName:
          `${profileData?.FirstName} ${profileData?.LastName}` || "",
        CustomerMobile: profileData?.PhoneNumber || "",
        OriginalSumInsured: originalSumInsured ?? "",
        RequestedSumInsured: adjustSumInsuredResponse?.NewPremium ?? "",
        PremiumDifference: adjustSumInsuredResponse.EndorsementFeesTotal ?? "",
        VehicleDetails: vehicleDetails ?? "",
      };

      try {
        // Calling API policy/pay
        const resultAction = await dispatch(endEndorsement(payload));
        const result = unwrapResult(resultAction);
        // console.log("Link is: ", link)
        if (result) {
          await fetch(`${BASE_URL}/MailToCompany/SendEmailAdjustSumInsured`, {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
            },
            body: JSON.stringify(emailPayload),
          });

          toast.success("Endorsement completed successfully", {
            duration: 5000,
          });
          navigate(
            `/insurance-policies/policy-documents?policy=${policyNumber}`
          ); // 🔁 Change to your target URL
        }
      } catch (error) {
        // alert("Error in getting payment link: ", error)
        toast.error("Error in getting payment link: ", error);
      }
    }
  };

  return (
    <AdjustSumInsuredMotorLayout is_bottom_sticky_layout={false}>
      <div className="adjustsumvalue p-4 w-[632px] max-w-full mx-auto mb-0">
        <TitleDescription
          title={t(
            "motorInsurance.adjustSumInsuredFlow.reviewAndConfirmPage.title"
          )}
          description={t(
            "motorInsurance.adjustSumInsuredFlow.reviewAndConfirmPage.description"
          )}
        />
        <div className="flex flex-col items-center mx-auto max-w-lg">
          <div className="mt-2 bg-[#F6F8F9] p-5 rounded-2xl w-full max767:bg-[#FFF] max767:p-0">
            <p className="text-[#48535B] text-base not-italic font-semibold leading-6 tracking-[-0.16px] mb-3">
              {t(
                "motorInsurance.adjustSumInsuredFlow.reviewAndConfirmPage.you_have_increased_label"
              )}
            </p>
            <div className="amountInput reviwsumvalue mb-4">
              <Input
                className="initialAmount w-full border border-solid border-[#DDE2E4] flex justify-center items-center gap-2.5 self-stretch pl-12 px-4 py-3 bg-[#E5E9EB] text-[#6E7C87] text-lg not-italic font-normal leading-[26px] tracking-[-0.18px] rounded-[10px] border-solid border-[#DDE2E4] max767:mb-0"
                type="number"
                name="amount"
                value={policyViewResponseDetails?.PolicyHeader?.OldSumInsured}
                startAdornment={
                  <p className="text-[#6E7C87] text-lg not-italic font-normal leading-[26px] tracking-[-0.18px">
                    {" "}
                    {translate("BHD", "د.ب")}
                  </p>
                }
              />
              <div className="w-0.5 h-8 shrink-0 bg-[#E5E9EB] ml-[25px]"></div>
              <Input
                className="initialAmount w-full bord er border-solid border-[#DDE2E4] flex justify-center items-center gap-2.5 self-stretch pl-12 px-4 py-3 bg-[#E5E9EB] text-[#6E7C87] text-lg not-italic font-normal leading-[26px] tracking-[-0.18px] rounded-[10px] border-solid border-[#DDE2E4] max767:mb-0"
                type="number"
                name="amount"
                value={adjustSumInsuredResponse?.NewPremium}
                startAdornment={
                  <p className="text-[#6E7C87] text-lg not-italic font-normal leading-[26px] tracking-[-0.18px">
                    {" "}
                    {translate("BHD", "د.ب")}
                  </p>
                }
              />
            </div>
            <ReviewMotorTable passedFrom={"MotorAdjustSumInsured"} />
            <div className="bg-[#F6F8F9] rounded-[26px] p-4 mb-4 w-full summary-box">
              {/* <PromoCode
                promoCode={formState.promoCode}
                setPromoCode={(code) =>
                  setFormState((prevState) => ({
                    ...prevState,
                    promoCode: code,
                  }))
                }
                applyPromoCode={applyPromoCode}
                clearPromoCode={() => {
                  setFormState({});
                  removeMotorFormData({});
                }}
              /> */}

              {adjustSumInsuredResponse.EndorsementFeesTotal > 0 && (
                <Summary passedFrom={"MotorAdjustSumInsured"} />
              )}

              <div className="flex">
                <CustomCheckbox
                  Label={
                    <div className="flex">
                      <label
                        htmlFor="agreement"
                        className="text-[#252C32] text-sm not-italic font-normal leading-6"
                      >
                        {declaration?.map((paragraph, index) => (
                          <p className="mb-2" key={index}>
                            {paragraph}
                          </p>
                        ))}
                      </label>
                    </div>
                  }
                  isChecked={isChecked}
                  setIsChecked={setIsChecked}
                />
              </div>

              {/* Proceed Button */}
              {adjustSumInsuredResponse.EndorsementFeesTotal <= 0 ? (
                <Button
                  isLoading={policyEndEndorsementLoading}
                  className={`bg-[#55C6EF] mt-4 w-full text-[#48535B] text-lg not-italic font-semibold leading-6 tracking-[-0.18px] flex justify-center p-3 rounded-[10px]`}
                  onSubmit={handleNoPayment}
                  label={t(
                    "motorInsurance.adjustSumInsuredFlow.reviewAndConfirmPage.proceed_to_no_payment"
                  )}
                  disabled={!isChecked}
                  post_icon={forwardarrow}
                  post_icon_class={"w-6 h-6 ml-[5px] rtl:rotate-180 rtl:mr-2"}
                />
              ) : (
                <Button
                  isLoading={policyPayLoading}
                  className={`bg-[#55C6EF] mt-4 w-full text-[#48535B] text-lg not-italic font-semibold leading-6 tracking-[-0.18px] flex justify-center p-3 rounded-[10px]`}
                  onSubmit={handlePayment}
                  disabled={!isChecked}
                  label={t(
                    "motorInsurance.adjustSumInsuredFlow.reviewAndConfirmPage.proceed_to_payment"
                  )}
                  post_icon={forwardarrow}
                  post_icon_class={"w-6 h-6 ml-[5px] rtl:rotate-180 rtl:mr-2"}
                />
              )}
            </div>
          </div>
        </div>
      </div>
    </AdjustSumInsuredMotorLayout>
  );
};

export default ReviewAndConfirmPage;
